import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaArrowLeft } from 'react-icons/fa6';
import Lottie from 'lottie-react';
import { toast } from 'react-toastify';

import Header from '../../components/Header';
import PromoBottomNavbar from '../../components/PromoBottomNavbar';
import MediumLoader from '../../components/Loaders/MediumLoader';
import leaderboardAnimation from '../../animations/leaderboardAnimation.json';
import { dailyLeaderboardApi } from '../../http/http';
import { removeUserToken } from '../../store/slices/userTokenSlice';

import classes from './PromoLeaderboard.module.css';
import { useTranslation } from 'react-i18next';

// The board is live, so it refreshes while the screen is open.
const REFRESH_MS = 30 * 1000;

// Today's date in Congo as 'YYYY-MM-DD', whatever the phone's timezone is.
// en-CA formats as YYYY-MM-DD, so no date library is needed.
const congoDate = () =>
    new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Africa/Brazzaville'
    }).format(new Date());

const PromoLeaderboard = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { userToken } = useSelector(state => state.persisted.userTokenSlice);

    const [leaderBoardList, setLeaderBoardList] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);

    const {t}=useTranslation();

    const fetchLeaderboard = async ({ silent = false } = {}) => {
        try {
            if (!silent) setLoading(true);

            // Recomputed on every call so the board rolls over correctly
            // if the screen stays open past Congo midnight.
            const raw = await dailyLeaderboardApi(congoDate(), userToken);

            // Works whether http.js returns the axios response or res.data.
            const payload = raw?.data ?? raw;
            const result = payload?.result ?? payload;

            setLeaderBoardList(
                Array.isArray(result?.leaderboard) ? result.leaderboard : []
            );
            setMeta(result ?? null);
        } catch (error) {
            if (error?.response?.status == 403) {
                dispatch(removeUserToken());
                toast.error(t("52"));
                navigate('/');
                return;
            }
            toast.error(
                t("86")
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeaderboard();

        const timer = setInterval(
            () => fetchLeaderboard({ silent: true }),
            REFRESH_MS
        );
        return () => clearInterval(timer);
    }, []);

    const prizeWinners = meta?.day_name === 'Sunday' ? 6 : 3;

    return (
        <>
            <Header />

            <div className={classes.container}>
                <div className={classes.header_section}>
                    <div
                        className={classes.back_button}
                        onClick={() => navigate(-1)}
                    >
                        <FaArrowLeft className={classes.back_icon} />
                    </div>

                    <h2 className={classes.header_text}>{t("55")}</h2>
                </div>

                <div className={classes.meta_row}>
                    <p className={classes.date}>
                        {meta ? `${meta.day_name}, ${meta.day_date}` : t("126")}
                    </p>
                    {meta?.current_datetime && (
                        <p className={classes.updated_at}>
                            {t("127")} {meta.current_datetime.slice(11, 16)}
                        </p>
                    )}
                </div>

                <p className={classes.prize_note}>
                    {t("128")} {prizeWinners} {t("129")}
                    {prizeWinners === 6 && ` \u2014 ${t('130')}`}
                </p>

                <div className={classes.leaderboard_container}>
                    <Lottie
                        animationData={leaderboardAnimation}
                        loop
                        autoPlay
                        className={classes.leaderboard_animation}
                    />

                    {loading ? (
                        <MediumLoader />
                    ) : leaderBoardList.length === 0 ? (
                        <p className={classes.empty_state}>
                            {t("131")}
                        </p>
                    ) : (
                        leaderBoardList.map((user, index) => {
                            const rank = user?.user_rank ?? index + 1;
                            const inPrizeRange = rank <= prizeWinners;

                            return (
                                <div
                                    className={`${classes.leaderboard_item} ${inPrizeRange ? classes.in_prize : ''}`}
                                    key={user?.msisdn ?? index}
                                >
                                    <div className={classes.leaderboard_section_one}>
                                        <div className={classes.user_profile_container}>
                                            <img
                                                src={user?.image || '/assets/images/dummy.png'}
                                                alt=""
                                                className={classes.user_profile_image}
                                            />
                                            <span className={classes.rank_badge}>
                                                {rank}
                                            </span>
                                        </div>

                                        <div className={classes.user_details_container}>
                                            <h4 className={classes.user_name}>
                                                {user?.name || 'Player'}
                                            </h4>
                                            <p className={classes.user_score}>
                                                {inPrizeRange
                                                    ? 'In the prize places'
                                                    : `Rank ${rank}`}
                                            </p>
                                        </div>
                                    </div>

                                    <div className={classes.user_points_container}>
                                        <img
                                            src="/assets/images/tag.png"
                                            alt=""
                                            className={classes.tag_image}
                                        />
                                        <span className={classes.user_points}>
                                            {user?.total_points ?? 0}
                                        </span>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            <PromoBottomNavbar active={3} />
        </>
    );
};

export default PromoLeaderboard;